import { computed } from "vue"
import { useStore } from "vuex"
import { useRouter, useRoute } from "vue-router"
import { useLocalStorage } from "@vueuse/core"
import { authClient } from "@/api/clients"
import errorHandler from "@/services/errorHandler"
import authStorage from "@/services/authStorage"
import { requestAuthTokenFromOtherTab } from "@/services/crossTabAuthBridge"
import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_USE_AUTH } from "@/constants/topology"
import logger from "@/services/logger"
import {
  AUTH_LOGIN_START,
  AUTH_LOGIN_COMPLETE,
  AUTH_LOGIN_FAIL,
  AUTH_LOGOUT_START,
  AUTH_LOGOUT_COMPLETE,
  AUTH_TOKEN_STORED,
  AUTH_TOKEN_CLEARED,
  AUTH_CHECK_START,
  AUTH_CHECK_COMPLETE,
  AUTH_CHECK_FAIL,
  AUTH_STATUS_TRANSITION,
} from "@/constants/events"

const INTENDED_DESTINATION_KEY = "intendedDestination"

// LLM:METADATA
// :topology: [Domain:Authentication | Role:Composable | Pattern:Facade]
// :relates-to: uses: "api", uses: "store", required-by: "App.vue", "Login.vue", "Sidebar.vue"
// :rationale: "Centralizes authentication state and actions (login, logout, check token) to remove logic from UI components."
// :contract: pre: "Valid store and router context", post: "Exposes reactive auth state and methods for login/logout", invariant: "All auth operations emit semantic events"
// LLM:END

export function useAuth() {
  setTopologyId(TOPOLOGY_USE_AUTH)
  const store = useStore()
  const router = useRouter()
  const route = useRoute()

  const isAuthenticated = computed(() => !!store.getters.getUserRole())
  const userRole = computed(() => store.getters.getUserRole())
  const userId = computed(() => store.getters.getUserId())

  // Reactive localStorage refs using VueUse (userId/userRole only)
  // Token is handled by authStorage adapter for backward compatibility
  const storedUserId = useLocalStorage("userId", null)
  const storedUserRole = useLocalStorage("userRole", "")

  /**
   * Persist the backend RBAC authority payload into Vuex after login or token refresh.
   * @precondition response is a normalized auth client payload.
   * @postcondition access profile, permissions version, and effective role reflect the latest backend authority surface.
   * @mutates Vuex auth module state.
   */
  const syncBackendAccessAuthority = (response) => {
    const normalizedPermissionsVersion = Number(response?.permissionsVersion)
    const accessProfile = response?.accessProfile

    // A checked token is not sufficient to establish frontend authority.
    // Missing or invalid backend authority must clear any stale session state.
    if (
      !accessProfile ||
      typeof accessProfile !== "object" ||
      Array.isArray(accessProfile) ||
      !Number.isFinite(normalizedPermissionsVersion) ||
      normalizedPermissionsVersion <= 0
    ) {
      clearFrontendSessionState({ preserveDestination: true })
      return false
    }

    const normalizedEffectiveRole =
      response?.effectiveRole || accessProfile.role_code || null

    store.dispatch("setAccessProfile", accessProfile)
    store.dispatch("setPermissionsVersion", normalizedPermissionsVersion)
    store.dispatch("setEffectiveRole", normalizedEffectiveRole)
    return true
  }

  /**
   * Clear every frontend auth authority seam so logout and token failures follow one cleanup contract.
   * @precondition Called for explicit logout or invalid-session handling.
   * @postcondition Vuex auth state, compatibility localStorage keys, and token storage are cleared.
   * @mutates Vuex auth state, browser storage, and redirect intent storage.
   */
  const clearFrontendSessionState = ({ preserveDestination = false } = {}) => {
    if (!preserveDestination) {
      localStorage.removeItem(INTENDED_DESTINATION_KEY)
    }

    store.dispatch("setUserRole", "")
    store.dispatch("setUserId", "")
    store.dispatch("setAccessProfile", null)
    store.dispatch("setPermissionsVersion", 0)
    store.dispatch("setEffectiveRole", null)
    storedUserId.value = null
    storedUserRole.value = ""
    authStorage.clearAuthStorage()
  }

  /**
   * Persist the current protected URL as post-login redirect intent.
   * @precondition Must not be called from the login route to avoid redirect loops.
   * @postcondition localStorage[intendedDestination] equals the latest protected URL (path+query+hash preserved).
   * @sideeffects Mutates browser localStorage by overwriting the previous preserved destination.
   * @throws Never throws; storage access failures are swallowed to keep auth flow alive.
   */
  const storeIntendedDestination = () => {
    try {
      localStorage.setItem(INTENDED_DESTINATION_KEY, window.location.href)
    } catch (error) {
      logger.warn(AUTH_CHECK_FAIL, "Failed to store intended destination", {
        error: error?.message,
      })
    }
  }

  /**
   * Check validity of the current token stored in localStorage
   * Redirects to login if invalid
   * LLM:METADATA
   * :topology: [Domain:Authentication | Role:TokenValidator | Pattern:StateTransition]
   * :relates-to: uses: "api", uses: "store", required-by: "App.vue", "router/index.js"
   * :rationale: "Validates token and transitions auth status through state machine"
   * :contract: pre: "Auth status is unknown or checking", post: "Auth status is authenticated or unauthenticated", invariant: "Status always transitions through checking state"
   * :decision_cache: "Chose state machine transitions for router guard synchronization [AUTH-CHECK-001]"
   // LLM:END
   */
  const checkToken = async () => {
    // Skip if on login page
    if (route.name === "login" || route.path === "/login") {
      // Still set status to unauthenticated if on login page without auth
      if (!store.getters.getUserRole()) {
        store.dispatch("setAuthStatus", "unauthenticated")
      }
      return
    }

    let token = authStorage.getToken()
    const role = storedUserRole.value

    // Cross-tab auth bridge: new tab with localStorage userRole but no sessionStorage token
    // Try to request token from an authenticated tab before redirecting to login
    if (!token && role) {
      logger.info(
        AUTH_STATUS_TRANSITION,
        "New tab detected: userRole in localStorage but no token in sessionStorage, attempting cross-tab bridge",
        {
          hasToken: !!token,
          hasRole: !!role,
          role: role,
        }
      )

      // Try to get token from another authenticated tab via BroadcastChannel bridge
      const bridgeSuccess = await requestAuthTokenFromOtherTab()

      if (bridgeSuccess) {
        // Token received from other tab, update local state
        token = authStorage.getToken()
        logger.info(
          AUTH_STATUS_TRANSITION,
          "Cross-tab bridge succeeded, token received from authenticated tab",
          {
            hasToken: !!token,
            hasRole: !!role,
          }
        )
      } else {
        // No authenticated tab responded, proceed to login
        logger.info(
          AUTH_STATUS_TRANSITION,
          "Cross-tab bridge failed: no authenticated tab responded",
          {
            hasToken: !!token,
            hasRole: !!role,
          }
        )
      }
    }

    if (!token || !role) {
      logger.info(
        AUTH_STATUS_TRANSITION,
        "No token or role found, transitioning to unauthenticated",
        {
          hasToken: !!token,
          hasRole: !!role,
        }
      )
      store.dispatch("setAuthStatus", "unauthenticated")
      redirectToLogin({ preserveDestination: true })
      return
    }

    // Transition to checking state
    logger.info(
      AUTH_STATUS_TRANSITION,
      "Transitioning auth status to checking",
      {
        from: store.getters.getAuthStatus(),
        to: "checking",
      }
    )
    store.dispatch("setAuthStatus", "checking")

    logger.info(AUTH_CHECK_START, "Checking token validity", {
      hasToken: !!token,
      hasRole: !!role,
    })

    try {
      const response = await authClient.checkToken(token, role)

      if (response.checked) {
        // Restore session in store
        if (!store.getters.getUserRole()) {
          if (storedUserRole.value) {
            store.dispatch("setUserRole", storedUserRole.value)
          }
          if (storedUserId.value) {
            const parsedUserId = parseInt(storedUserId.value)
            if (!isNaN(parsedUserId)) {
              store.dispatch("setUserId", parsedUserId)
            }
          }
        }

        if (!syncBackendAccessAuthority(response)) {
          store.dispatch("setAuthStatus", "unauthenticated")
          await clearSessionAndRedirectToLogin({ preserveDestination: true })
          return
        }

        // Transition to authenticated state
        logger.info(
          AUTH_STATUS_TRANSITION,
          "Transitioning auth status to authenticated",
          {
            from: store.getters.getAuthStatus(),
            to: "authenticated",
          }
        )
        store.dispatch("setAuthStatus", "authenticated")

        logger.info(AUTH_CHECK_COMPLETE, "Token validated successfully", {
          userId: storedUserId.value ? parseInt(storedUserId.value) : null,
          role: storedUserRole.value,
        })
      } else {
        // Transition to unauthenticated state
        logger.info(
          AUTH_STATUS_TRANSITION,
          "Token check failed, transitioning to unauthenticated",
          {
            from: store.getters.getAuthStatus(),
            to: "unauthenticated",
          }
        )
        store.dispatch("setAuthStatus", "unauthenticated")
        await clearSessionAndRedirectToLogin({ preserveDestination: true })
      }
    } catch (error) {
      // 401 interceptor might catch this, but safe fallback
      logger.error(AUTH_CHECK_FAIL, "Token check failed", {
        error: error.message,
        stack: error.stack,
      })

      // Transition to unauthenticated state on error
      logger.info(
        AUTH_STATUS_TRANSITION,
        "Token check error, transitioning to unauthenticated",
        {
          from: store.getters.getAuthStatus(),
          to: "unauthenticated",
          error: error.message,
        }
      )
      store.dispatch("setAuthStatus", "unauthenticated")

      if (error.name !== "AbortError") {
        await clearSessionAndRedirectToLogin({ preserveDestination: true })
      }
    }
  }

  /**
   * Perform login with email and password
   * LLM:METADATA
   * :topology: [Domain:Authentication | Role:LoginHandler | Pattern:StateTransition]
   * :relates-to: uses: "api", uses: "store", required-by: "Login.vue"
   * :rationale: "Handles user login and transitions auth status to authenticated on success"
   * :contract: pre: "Valid credentials provided", post: "Auth status is authenticated on success", invariant: "Status always transitions through checking during login"
   // LLM:END
   */
  const login = async (email, pass) => {
    // Set status to checking during login attempt
    logger.info(
      AUTH_STATUS_TRANSITION,
      "Login started, transitioning auth status to checking",
      {
        from: store.getters.getAuthStatus(),
        to: "checking",
      }
    )
    store.dispatch("setAuthStatus", "checking")

    logger.info(AUTH_LOGIN_START, "Login attempt started", {
      email: email.trim(),
    })

    try {
      const response = await authClient.login(email, pass)

      if (!response.success) {
        logger.error(AUTH_LOGIN_FAIL, "Login failed", {
          error: response.error,
        })

        // Transition to unauthenticated on failure
        logger.info(
          AUTH_STATUS_TRANSITION,
          "Login failed, transitioning auth status to unauthenticated",
          {
            from: store.getters.getAuthStatus(),
            to: "unauthenticated",
          }
        )
        store.dispatch("setAuthStatus", "unauthenticated")

        return { success: false, error: response.error }
      } else {
        // Success
        store.dispatch("setUserRole", response.role)
        store.dispatch("setUserId", response.id)
        if (!syncBackendAccessAuthority(response)) {
          store.dispatch("setAuthStatus", "unauthenticated")
          return {
            success: false,
            error: "Backend access authority is incomplete",
          }
        }
        storedUserId.value = response.id
        storedUserRole.value = response.role
        // Use authStorage adapter for backward compatibility (writes to both token and userRoleToken)
        authStorage.setToken(response.token)

        // Transition to authenticated on success
        logger.info(
          AUTH_STATUS_TRANSITION,
          "Login successful, transitioning auth status to authenticated",
          {
            from: store.getters.getAuthStatus(),
            to: "authenticated",
          }
        )
        store.dispatch("setAuthStatus", "authenticated")

        logger.info(AUTH_TOKEN_STORED, "Token stored in localStorage", {
          userId: response.id,
          role: response.role,
        })

        logger.info(AUTH_LOGIN_COMPLETE, "Login completed successfully", {
          userId: response.id,
          role: response.role,
        })

        return { success: true }
      }
    } catch (error) {
      console.error("[Login DIAG] Request failed", {
        message: error.message,
        code: error.code,
        isTimeout: error.code === "ECONNABORTED",
      })
      logger.error(AUTH_LOGIN_FAIL, "Login failed with exception", {
        error: error.message,
        stack: error.stack,
      })

      // Transition to unauthenticated on error
      logger.info(
        AUTH_STATUS_TRANSITION,
        "Login error, transitioning auth status to unauthenticated",
        {
          from: store.getters.getAuthStatus(),
          to: "unauthenticated",
        }
      )
      store.dispatch("setAuthStatus", "unauthenticated")

      errorHandler.handle(error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Logout user and clear session
   * LLM:METADATA
   * :topology: [Domain:Authentication | Role:LogoutHandler | Pattern:StateTransition]
   * :relates-to: uses: "router", uses: "store", required-by: "App.vue", "Sidebar.vue"
   * :rationale: "Handles logout and transitions auth status to unauthenticated"
   * :contract: pre: "User is authenticated", post: "Auth status is unauthenticated", invariant: "All auth data is cleared"
   // LLM:END
   */
  const logout = async () => {
    logger.info(AUTH_LOGOUT_START, "Logout started", {
      userId: userId.value,
      role: userRole.value,
    })

    try {
      await authClient.logout()
    } catch (error) {
      logger.warn(AUTH_LOGOUT_START, "Backend logout revocation failed", {
        error: error?.message,
      })
    }

    // Transition to unauthenticated
    logger.info(
      AUTH_STATUS_TRANSITION,
      "Logout started, transitioning auth status to unauthenticated",
      {
        from: store.getters.getAuthStatus(),
        to: "unauthenticated",
      }
    )
    store.dispatch("setAuthStatus", "unauthenticated")

    clearFrontendSessionState()

    logger.info(AUTH_TOKEN_CLEARED, "Token cleared from localStorage", {
      userId: userId.value,
    })

    await router.push("/login")

    logger.info(AUTH_LOGOUT_COMPLETE, "Logout completed", {
      userId: userId.value,
    })
  }

  /**
   * Redirect to login and optionally preserve the latest protected destination.
   * @precondition Route context is available.
   * @postcondition When preserveDestination=true and current route is not login, intendedDestination is stored before navigation.
   * @postcondition Redirect target remains `/login` for all unauthenticated flows.
   * @param {{preserveDestination?: boolean}} [options]
   */
  const redirectToLogin = ({ preserveDestination = false } = {}) => {
    if (route.name !== "login") {
      if (preserveDestination) {
        storeIntendedDestination()
      }
      router.push("/login")
    }
  }

  /**
   * Clear auth session and navigate to login while optionally keeping redirect intent.
   * @precondition Intended for token-invalid or explicit logout flows.
   * @postcondition Auth status is unauthenticated and auth token keys are cleared.
   * @postcondition intendedDestination is cleared unless preserveDestination=true.
   * @param {{preserveDestination?: boolean}} [options]
   */
  const clearSessionAndRedirectToLogin = async ({
    preserveDestination = false,
  } = {}) => {
    if (preserveDestination && route.name !== "login") {
      storeIntendedDestination()
    }

    logger.info(
      AUTH_STATUS_TRANSITION,
      "Logout started, transitioning auth status to unauthenticated",
      {
        from: store.getters.getAuthStatus(),
        to: "unauthenticated",
      }
    )
    store.dispatch("setAuthStatus", "unauthenticated")

    clearFrontendSessionState({ preserveDestination })

    await router.push("/login")
  }

  return {
    isAuthenticated,
    userRole,
    userId,
    checkToken,
    login,
    logout,
  }
}
